import { useNavigate } from 'react-router-dom'
import { Plus, MapPin } from 'lucide-react'
import { listSitePlans } from '../services/data'
import { PageHeader, Card, DataTable, SearchInput, Pagination, Spinner, Button } from '../components/shared/ui'
import { date } from '../lib/format'
import { useServerList } from '../lib/list'
import type { SitePlan } from '../types'

const PAGE_SIZE = 25

const STATUS_CHIP: Record<string, string> = {
  draft:    'bg-amber-100 text-amber-700',
  final:    'bg-emerald-100 text-emerald-700',
  archived: 'bg-gray-100 text-gray-500',
}

export default function SitePlans() {
  const navigate = useNavigate()
  const { rows, count, page, search, sort, loading, setPage, setSearch, toggleSort } =
    useServerList<SitePlan>(listSitePlans, { pageSize: PAGE_SIZE, defaultSort: { column: 'updated_at', dir: 'desc' } })

  return (
    <div>
      <PageHeader
        title="Site Plans"
        subtitle={`${count.toLocaleString()} designer site plans`}
        actions={<Button onClick={() => navigate('/site-plans/new')}><Plus size={16} /> New Site Plan</Button>}
      />
      <Card className="p-4">
        <div className="mb-4">
          <SearchInput value={search} onChange={setSearch} placeholder="Search by name, address, or customer…" />
        </div>
        {loading ? (
          <Spinner label="Loading site plans…" />
        ) : (
          <>
            <DataTable
              rows={rows}
              sort={sort}
              onSort={toggleSort}
              onRowClick={r => navigate(`/site-plans/${r.id}`)}
              columns={[
                {
                  key: 'name', header: 'Plan Name',
                  render: r => (
                    <div className="flex items-center gap-2">
                      <MapPin size={14} className="text-green-600 shrink-0" />
                      <span className="font-medium text-gray-900">{r.name}</span>
                    </div>
                  ),
                  sortKey: 'name',
                },
                { key: 'address', header: 'Address', render: r => r.address || '—', sortKey: 'address' },
                { key: 'customer_name', header: 'Customer', render: r => (r as any).customer_name || '—' },
                { key: 'version_label', header: 'Version', render: r => r.version_label || '—' },
                {
                  key: 'status', header: 'Status',
                  render: r => (
                    <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${STATUS_CHIP[r.status] ?? 'bg-gray-100 text-gray-500'}`}>
                      {r.status}
                    </span>
                  ),
                },
                { key: 'updated_at', header: 'Last Updated', render: r => date(r.updated_at), sortKey: 'updated_at' },
              ]}
            />
            <Pagination page={page} pageSize={PAGE_SIZE} total={count} onPage={setPage} />
          </>
        )}
      </Card>
    </div>
  )
}
